import { useMemo, type ReactNode } from "react";
import { Link } from "react-router";
import { ArrowRight, CalendarOff, Coffee, Palmtree, UserCheck } from "lucide-react";

import { Avatar } from "@/components/ui/Avatar";
import { EmptyState } from "@/components/ui/EmptyState";
import { SidePanel } from "@/components/ui/SidePanel";
import { Skeleton } from "@/components/ui/Skeleton";
import { Tabs } from "@/components/ui/Tabs";
import { cn } from "@/lib/cn";
import { formatDate, formatLongDate } from "@/lib/format";
import type {
  TodayBoard,
  TodayNotWorking,
  TodayOnDuty,
  TodayOnLeave,
  TodayPerson,
} from "@/features/dashboard/hooks";

export type TodayView = "on_duty" | "on_leave" | "rest_day" | "not_scheduled";

const VIEWS: readonly { value: TodayView; label: string }[] = [
  { value: "on_duty", label: "On duty" },
  { value: "on_leave", label: "On leave" },
  { value: "rest_day", label: "Rest day" },
  { value: "not_scheduled", label: "Not scheduled" },
];

/** How many faces the tile shows before it says "+12". */
const STACK = 5;

/**
 * The faces of the first few people on duty, and the way into the full list.
 *
 * Sits under the figure on the tinted tile, so it is drawn in the tile's own
 * colours: each face ringed in the tile's ground so the overlap reads as a
 * stack rather than a smudge.
 */
export function TodayFaces({
  board,
  onOpen,
}: {
  board: TodayBoard | undefined;
  onOpen: () => void;
}) {
  const people = board?.on_duty ?? [];
  const shown = people.slice(0, STACK);
  const more = people.length - shown.length;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "group -mx-1 -mb-1 flex items-center justify-between gap-3 rounded-md px-1 py-1 text-left",
        "text-board-text outline-none transition-colors hover:bg-board-soft",
        "focus-visible:ring-2 focus-visible:ring-board-muted",
      )}
    >
      <span className="flex items-center" aria-hidden="true">
        {shown.map((person, index) => (
          <Avatar
            key={person.user_id}
            name={person.name}
            fileId={person.photo_file_id}
            size="sm"
            className={cn("size-7 ring-2 ring-board", index > 0 && "-ml-2")}
          />
        ))}
        {more > 0 ? (
          <span className="-ml-2 grid h-7 min-w-7 place-items-center rounded-full bg-board-soft px-1.5 text-2xs font-bold tabular-nums ring-2 ring-board">
            +{more}
          </span>
        ) : null}
      </span>
      <span className="flex items-center gap-1 text-xs font-semibold">
        See who
        <ArrowRight
          className="size-3.5 transition-transform duration-150 group-hover:translate-x-0.5"
          aria-hidden="true"
        />
      </span>
    </button>
  );
}

/**
 * Everybody active, in the one place they are today.
 *
 * Four lists rather than one: who is in, who is on leave, whose day off it
 * is, and who is none of these — the last is the one worth a question. Each
 * is grouped by branch, because "who is at Karama" is how HR asks it.
 */
export function TodayPanel({
  open,
  onOpenChange,
  board,
  loading,
  view,
  onViewChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  board: TodayBoard | undefined;
  loading: boolean;
  view: TodayView;
  onViewChange: (view: TodayView) => void;
}) {
  const counts: Record<TodayView, number> = {
    on_duty: board?.on_duty.length ?? 0,
    on_leave: board?.on_leave.length ?? 0,
    rest_day: board?.rest_day.length ?? 0,
    not_scheduled: board?.not_scheduled.length ?? 0,
  };

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title="Who is in today"
      subtitle={
        board
          ? `${formatLongDate(board.date)} · ${
              board.rota_drawn
                ? "From today's rota, and the records of anybody not on it"
                : "No rota drawn for today, so from each person's working days"
            }`
          : undefined
      }
      size="lg"
    >
      <Tabs
        label="Who is in today"
        value={view}
        onChange={onViewChange}
        items={VIEWS.map((item) => ({
          ...item,
          count: counts[item.value],
          countWaiting: item.value === "not_scheduled" && counts.not_scheduled > 0,
        }))}
        className="-mx-5 px-5"
      />
      <div className="pt-4">
        {loading || !board ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <TodayList board={board} view={view} />
        )}
      </div>
    </SidePanel>
  );
}

function TodayList({ board, view }: { board: TodayBoard; view: TodayView }) {
  const rows: TodayPerson[] = board[view];
  const groups = useMemo(() => byBranch(rows, view), [rows, view]);

  if (rows.length === 0) {
    const empty: Record<TodayView, { title: string; description: string; icon: ReactNode }> = {
      on_duty: {
        title: "Nobody is on duty today",
        description: "Nobody is on today's rota, and nobody's record has them working today.",
        icon: <UserCheck className="size-5" />,
      },
      on_leave: {
        title: "Nobody is on leave today",
        description: "Approved full-day leave shows here. Requests still waiting do not.",
        icon: <Palmtree className="size-5" />,
      },
      rest_day: {
        title: "Nobody has a day off today",
        description: "Weekly days off, and rota days off, show here.",
        icon: <Coffee className="size-5" />,
      },
      not_scheduled: {
        title: "Everybody is accounted for",
        description: "Everybody active is on duty, on leave or on their day off.",
        icon: <CalendarOff className="size-5" />,
      },
    };
    const { title, description, icon } = empty[view];
    return <EmptyState title={title} description={description} icon={icon} />;
  }

  return (
    <div className="space-y-5">
      {groups.map(([branch, people]) => (
        <section key={branch} aria-label={branch}>
          <h3 className="mb-1 flex items-baseline justify-between gap-3 border-b border-line pb-1.5">
            <span className="u-label">{branch}</span>
            <span className="text-xs tabular-nums text-fg-subtle">{people.length}</span>
          </h3>
          <ul className="divide-y divide-line">
            {people.map((person) => (
              <li key={person.user_id}>
                <PersonRow person={person} view={view} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function PersonRow({ person, view }: { person: TodayPerson; view: TodayView }) {
  return (
    <Link
      to={`/admin/employees/${person.user_id}`}
      className="-mx-2 flex items-center gap-3 rounded-md px-2 py-2.5 outline-none transition-colors hover:bg-ink-50 focus-visible:ring-2 focus-visible:ring-accent"
    >
      <Avatar name={person.name} fileId={person.photo_file_id} size="sm" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-base font-medium text-fg">{person.name}</span>
        <span className="block truncate text-xs text-fg-subtle">
          {[person.position, person.employee_number].filter(Boolean).join(" · ") || "—"}
        </span>
      </span>
      <span className="shrink-0 text-right">{detail(person, view)}</span>
    </Link>
  );
}

function detail(person: TodayPerson, view: TodayView): ReactNode {
  if (view === "on_duty") {
    const row = person as TodayOnDuty;
    const notes = [
      row.covering_from ? `Covering from ${row.covering_from}` : null,
      row.on_rest_day ? "On their day off" : null,
    ].filter(Boolean);
    return (
      <>
        <span className="block text-sm font-medium tabular-nums text-fg">
          {row.start_time && row.end_time
            ? `${hhmm(row.start_time)}–${hhmm(row.end_time)}${row.ends_next_day ? " +1" : ""}`
            : "Usual hours"}
        </span>
        <span className="block text-xs text-fg-subtle">
          {notes.length > 0 ? notes.join(" · ") : (row.shift_name ?? "Not on a rota")}
        </span>
      </>
    );
  }
  if (view === "on_leave") {
    const row = person as TodayOnLeave;
    return (
      <>
        <span className="block text-sm font-medium text-fg">{row.leave_type}</span>
        <span className="block text-xs text-fg-subtle">
          {row.until ? `Until ${formatDate(row.until)}` : "—"}
        </span>
      </>
    );
  }
  return <span className="block max-w-48 text-xs text-fg-subtle">{(person as TodayNotWorking).reason}</span>;
}

function hhmm(value: string): string {
  return value.slice(0, 5);
}

const NO_BRANCH = "Head office, or no branch";

/** Branches by name, people by shift then name; anybody without a branch last. */
function byBranch(rows: TodayPerson[], view: TodayView): [string, TodayPerson[]][] {
  const groups = new Map<string, TodayPerson[]>();
  for (const row of rows) {
    const key = row.branch_name ?? NO_BRANCH;
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }
  const sorted = [...groups.entries()].sort(([a], [b]) => {
    if (a === NO_BRANCH) return 1;
    if (b === NO_BRANCH) return -1;
    return a.localeCompare(b);
  });
  if (view === "on_duty") {
    for (const [, people] of sorted) {
      people.sort((a, b) => {
        const left = (a as TodayOnDuty).start_time ?? "99";
        const right = (b as TodayOnDuty).start_time ?? "99";
        return left.localeCompare(right) || a.name.localeCompare(b.name);
      });
    }
  }
  return sorted;
}
